import { createSHA256 } from 'hash-wasm';
import {
  decryptAesGcm,
  encodeBase64Url,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  sha256,
  unpackEncryptedBlob,
} from './primitives';

export const DEFAULT_CHUNK_PLAINTEXT_BYTES = 32 * 1024 * 1024;

export interface ChunkedFileChunkManifest {
  chunkIndex: number;
  ciphertextSizeBytes: number;
}

export interface ChunkedFileManifest {
  fileId: string;
  contentKey: Uint8Array;
  plaintextSha256: string;
  totalPlaintextBytes: number;
  chunkPlaintextBytes: number;
  chunks: ChunkedFileChunkManifest[];
}

export interface EncryptedChunkUpload {
  chunkIndex: number;
  ciphertext: Uint8Array;
}

export interface EncryptBlobChunkRange {
  start: number;
  end: number;
}

export interface EncryptBlobInChunksOptions {
  contentKey?: Uint8Array;
  collectChunks?: boolean;
  onChunk?: (chunk: EncryptedChunkUpload, range: EncryptBlobChunkRange) => Promise<void> | void;
  onProgress?: (bytesCompleted: number, bytesTotal: number) => void;
}

function chunkAad(fileId: string, chunkIndex: number): Uint8Array {
  const fileBytes = new TextEncoder().encode(fileId);
  const indexBytes = new Uint8Array(4);
  new DataView(indexBytes.buffer).setUint32(0, chunkIndex, false);
  const aad = new Uint8Array(fileBytes.length + indexBytes.length);
  aad.set(fileBytes, 0);
  aad.set(indexBytes, fileBytes.length);
  return aad;
}

export async function encryptBlobInChunks(
  fileId: string,
  blob: Blob,
  chunkPlaintextBytes = DEFAULT_CHUNK_PLAINTEXT_BYTES,
  options: EncryptBlobInChunksOptions = {},
): Promise<{ manifest: ChunkedFileManifest; encryptedChunks: EncryptedChunkUpload[] }> {
  const contentKey = options.contentKey ?? randomBytes(32);
  const key = await importAesKey(contentKey);
  const hasher = await createSHA256();
  hasher.init();

  const encryptedChunks: EncryptedChunkUpload[] = [];
  const chunks: ChunkedFileChunkManifest[] = [];
  const collectChunks = options.collectChunks !== false;
  const total = blob.size;

  const emitChunk = async (
    chunkIndex: number,
    packed: Uint8Array,
    range: EncryptBlobChunkRange,
  ) => {
    const chunk = { chunkIndex, ciphertext: packed };
    chunks.push({ chunkIndex, ciphertextSizeBytes: packed.length });
    if (collectChunks) {
      encryptedChunks.push(chunk);
    }
    await options.onChunk?.(chunk, range);
  };

  if (total === 0) {
    options.onProgress?.(0, 0);
    hasher.update(new Uint8Array(0));
    const encrypted = await encryptAesGcm(key, new Uint8Array(0), chunkAad(fileId, 0));
    await emitChunk(0, packEncryptedBlob(encrypted), { start: 0, end: 0 });
  } else {
    for (let offset = 0, chunkIndex = 0; offset < total; chunkIndex++) {
      const end = Math.min(offset + chunkPlaintextBytes, total);
      options.onProgress?.(offset, total);
      const slice = new Uint8Array(await blob.slice(offset, end).arrayBuffer());
      hasher.update(slice);
      const encrypted = await encryptAesGcm(key, slice, chunkAad(fileId, chunkIndex));
      await emitChunk(chunkIndex, packEncryptedBlob(encrypted), { start: offset, end });
      options.onProgress?.(end, total);
      offset = end;
    }
  }

  return {
    manifest: {
      fileId,
      contentKey,
      plaintextSha256: encodeBase64Url(hasher.digest('binary')),
      totalPlaintextBytes: total,
      chunkPlaintextBytes,
      chunks,
    },
    encryptedChunks,
  };
}

export async function encryptFileInChunks(
  fileId: string,
  plaintext: Uint8Array,
  chunkPlaintextBytes = DEFAULT_CHUNK_PLAINTEXT_BYTES,
  contentKey?: Uint8Array,
): Promise<{ manifest: ChunkedFileManifest; encryptedChunks: EncryptedChunkUpload[] }> {
  const copy = new Uint8Array(plaintext);
  return encryptBlobInChunks(fileId, new Blob([copy]), chunkPlaintextBytes, { contentKey });
}

export async function decryptFileFromChunks(
  manifest: ChunkedFileManifest,
  encryptedChunks: Map<number, Uint8Array>,
): Promise<Uint8Array> {
  const key = await importAesKey(manifest.contentKey);
  const parts: Uint8Array[] = [];
  let totalLength = 0;

  for (const chunk of manifest.chunks) {
    const packed = encryptedChunks.get(chunk.chunkIndex);
    if (!packed) {
      throw new Error('missing_chunk');
    }

    const blob = unpackEncryptedBlob(packed);
    const plaintext = await decryptAesGcm(key, blob, chunkAad(manifest.fileId, chunk.chunkIndex));
    parts.push(plaintext);
    totalLength += plaintext.length;
  }

  const output = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }

  const digest = await sha256(output);
  if (encodeBase64Url(digest) !== manifest.plaintextSha256) {
    throw new Error('plaintext_hash_mismatch');
  }

  return output;
}

export function computeDownloadChunkCount(totalCiphertextBytes: number, chunkSizeBytes: number): number {
  if (totalCiphertextBytes <= 0) {
    return 1;
  }

  return Math.ceil(totalCiphertextBytes / chunkSizeBytes);
}

export function splitPackedChunks(
  blob: Uint8Array,
  sizes: number[],
): Map<number, Uint8Array> {
  const map = new Map<number, Uint8Array>();
  let offset = 0;
  for (let index = 0; index < sizes.length; index++) {
    const size = sizes[index] ?? 0;
    map.set(index, blob.subarray(offset, offset + size));
    offset += size;
  }

  if (offset !== blob.length) {
    throw new Error('ciphertext_size_mismatch');
  }

  return map;
}
